<template>
    <b-modal id="mcqImportExcelModal" size="xl" hide-footer no-close-on-backdrop body-class="p-0"
        header-class="mcq-import-header" @hidden="removeExcelSheet">
        <template #modal-title>
            <i class="fas fa-file-excel text-success mr-2"></i>Import Questions for:
            <span class="text-info">{{ exam.name }}</span>
        </template>

        <div class="mcq-import">
            <!-- File selection  -->
            <div class="mcq-import-file">
                <b-form-file ref="selectedExcelFile" style="display: none" v-model="excelSheet" accept=".xlsx"
                    @input="readExcelSheet"></b-form-file>
                <template v-if="!excelSheet">
                    <b-button variant="info" pill size="sm" class="mcq-btn"
                        @click="$refs.selectedExcelFile.$refs.input.click()">
                        <i class="fas fa-file-excel mr-1"></i> Select Excel File
                    </b-button>
                    <b-button variant="outline-secondary" size="sm" class="mcq-btn ml-2" @click="downloadSample">
                        <i class="fas fa-download mr-1"></i> Download sample
                    </b-button>
                </template>
                <div v-else class="mcq-import-file-name">
                    <i class="fas fa-file-excel text-success mr-1"></i>{{ excelSheet.name }}
                    <span v-if="!saving" class="text-danger btn m-0 p-0 ml-2" title="Remove"
                        @click="removeExcelSheet"><i class="fas fa-trash"></i></span>
                </div>
            </div>

            <!-- Excel format  -->
            <div class="mcq-import-note" v-if="!questions.length">
                <table class="table table-bordered table-sm mb-0 mcq-import-format">
                    <thead>
                        <tr>
                            <th v-for="header in sampleHeaders" :key="header">{{ header }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in sampleRows" :key="row[0]">
                            <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="mcq-import-loading" v-if="reading">
                <b-spinner small variant="primary" class="mr-2"></b-spinner> Reading excel sheet...
            </div>

            <!-- Questions from excel  -->
            <div v-if="questions.length">
                <div class="mcq-import-summary">
                    <span class="mcq-chip">{{ questions.length }} Questions</span>
                    <span class="mcq-chip mcq-chip-danger" v-if="noRightCount">Choose the right answer for each
                        question ({{ noRightCount }} left)</span>
                    <span class="mcq-chip mcq-chip-danger" v-if="invalidCount > noRightCount">
                        {{ invalidCount - noRightCount }} need a fix</span>
                    <span class="mcq-chip mcq-chip-success" v-else>Ready to import</span>
                </div>

                <div v-for="(question, questionIndex) in questions" :key="question.key"
                    :class="['mcq-import-card', { 'mcq-import-card-error': rowError(question), 'mcq-import-card-done': question.saved }]">
                    <div class="mcq-import-card-head">
                        <b-form-input v-model="question.question_no" class="mcq-import-qno" placeholder="Q No"
                            :disabled="saving || question.saved"></b-form-input>
                        <b-form-input v-model="question.name" placeholder="Question" class="flex-grow-1"
                            :disabled="saving || question.saved"></b-form-input>
                        <template v-if="differentScores">
                            <b-form-input v-model="question.maximum_mark" class="mcq-import-score" placeholder="+ Score"
                                v-b-tooltip.hover title="Score for right answer" :disabled="saving || question.saved">
                            </b-form-input>
                            <b-form-input v-model="question.wrong_answer_mark" class="mcq-import-score"
                                placeholder="- Score" v-b-tooltip.hover title="Score for wrong answer"
                                :disabled="saving || question.saved"></b-form-input>
                        </template>
                        <span v-if="question.saved" class="text-success ml-2" title="Imported"><i
                                class="fas fa-check-circle"></i></span>
                        <span v-else-if="!saving" class="text-danger btn m-0 p-0 ml-2" title="Remove question"
                            @click="questions.splice(questionIndex, 1)"><i class="fas fa-minus-circle"></i></span>
                    </div>

                    <div class="mcq-import-choices">
                        <div v-for="(choice, choiceIndex) in question.choices" :key="choiceIndex"
                            :class="['mcq-import-choice', { 'mcq-choice-right': question.right == choiceIndex }]">
                            <b-form-radio v-model="question.right" :value="choiceIndex" :name="'right' + question.key"
                                :disabled="saving || question.saved" v-b-tooltip.hover title="Right answer">
                            </b-form-radio>
                            <b-form-input v-model="choice.choice_no" size="sm" class="mcq-import-choice-no"
                                :placeholder="choiceLabel(choiceIndex)" v-b-tooltip.hover title="Choice no"
                                :disabled="saving || question.saved"></b-form-input>
                            <b-form-input v-model="choice.name" size="sm" class="flex-grow-1"
                                :disabled="saving || question.saved"></b-form-input>
                            <span v-if="!saving && !question.saved" class="text-danger btn m-0 p-0 ml-2"
                                title="Remove option" @click="removeChoice(question, choiceIndex)"><i
                                    class="fas fa-trash"></i></span>
                        </div>
                        <b-button v-if="!saving && !question.saved" variant="link" size="sm" class="p-0 mcq-import-add"
                            @click="question.choices.push({ choice_no: '', name: '' })"><i class="fas fa-plus mr-1"></i>Add option</b-button>
                    </div>

                    <div class="mcq-import-error" v-if="rowError(question)">
                        <i class="fas fa-exclamation-triangle mr-1"></i>{{ rowError(question) }}
                    </div>
                </div>

                <div class="mcq-import-foot">
                    <span class="mcq-muted" v-if="saving">
                        <b-spinner small class="mr-1"></b-spinner> Importing {{ savedCount }} / {{ questions.length }}...
                    </span>
                    <span v-else></span>
                    <b-button variant="success" size="sm" class="mcq-btn" :disabled="saving || !!invalidCount"
                        @click="importQuestions">
                        <i class="fas fa-upload mr-1"></i> Import {{ questions.length }} Questions
                    </b-button>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import readXlsxFile from "read-excel-file";
import XLSX from "xlsx";

// excel columns, all options in one cell separated by comma or new line
const COL = { no: 0, question: 1, options: 2, right: 3, wrong: 4 };

const escapeHtml = (value) => String(value)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export default {
    // nextQuestionNo: question number for rows without one
    props: ["exam", "differentScores", "examScores", "nextQuestionNo"],
    data() {
        return {
            excelSheet: null,
            reading: false,
            saving: false,
            questions: [],
        };
    },
    computed: {
        // score columns only when every question has different scores
        sampleHeaders() {
            const headers = ["Q No", "Question", "Options"];
            return this.differentScores ? [...headers, "Score for right answer", "Score for wrong answer"] : headers;
        },
        sampleRows() {
            const row = (cells, right, wrong) => (this.differentScores ? [...cells, right, wrong] : cells);
            return [
                row([1, "Which of the following is a programming language?", "HTML, Python, CSS, SQL"], 5, 1),
                row([2, "Which planet is known as the red planet?", "Earth, Venus, Mars"], 3, 1),
                row([3, "HTML is a programming language.", "True, False"], 2, 0),
            ];
        },
        invalidCount() {
            return this.questions.filter((question) => !question.saved && this.rowError(question)).length;
        },
        // questions whose only problem is the right answer not chosen yet
        noRightCount() {
            return this.questions.filter((question) => this.rowError(question) == "Select the right answer").length;
        },
        savedCount() {
            return this.questions.filter((question) => question.saved).length;
        },
    },
    methods: {
        // choice no A, B, C..., index from 0
        choiceLabel(index) {
            return String.fromCharCode(65 + index);
        },
        cellText(cell) {
            return cell == null ? "" : String(cell).trim();
        },
        removeExcelSheet() {
            if (this.saving) return;
            this.excelSheet = null;
            this.questions = [];
        },
        removeChoice(question, choiceIndex) {
            question.choices.splice(choiceIndex, 1);
            if (question.right === choiceIndex) question.right = null;
            else if (question.right > choiceIndex) question.right--;
        },
        // "A) HTML", "(1) HTML", "i. HTML", "b - HTML" keep their own choice no, others get A, B, C...
        parseChoice(cell) {
            const text = this.cellText(cell);
            // "." and ":" need a space after them so "2.5 kg" or "a.m." stay as option text
            const match = text.match(/^\(?\s*([a-z]|\d{1,2}|[ivx]{1,5})\s*(?:[)\]]\s*|[.:]\s+|\s-\s*)(\S.*)$/i);
            return match ? { choice_no: match[1], name: match[2].trim() } : { choice_no: "", name: text };
        },
        // choice no saved for a choice, A, B, C... when the sheet had none
        choiceNo(choice, index) {
            return this.cellText(choice.choice_no) || this.choiceLabel(index);
        },
        async readExcelSheet() {
            if (!this.excelSheet) return;
            this.reading = true;
            this.questions = [];
            try {
                const rows = await readXlsxFile(this.excelSheet);
                let nextNo = Number(this.nextQuestionNo) || 1;
                rows.forEach((row, rowIndex) => {
                    // skip empty rows and the header row
                    if (!row.some((cell) => this.cellText(cell))) return;
                    if (rowIndex == 0 && !/^\d/.test(this.cellText(row[COL.no])) &&
                        /question/i.test(this.cellText(row[COL.question]))) return;

                    // options on separate lines in the cell, otherwise separated by comma
                    const optionsText = this.cellText(row[COL.options]);
                    const choices = optionsText.split(optionsText.includes("\n") ? /\r?\n/ : ",")
                        .map((option) => this.parseChoice(option))
                        .filter((choice) => choice.name);
                    this.questions.push({
                        key: rowIndex,
                        // question no is kept as written (1, Q1, 1a, ii...), only a trailing "." or ")" is dropped
                        question_no: this.cellText(row[COL.no]).replace(/[.)]$/, "") || `${nextNo}`,
                        name: this.cellText(row[COL.question]),
                        maximum_mark: this.cellText(row[COL.right]),
                        wrong_answer_mark: this.cellText(row[COL.wrong]).replace(/^-/, ""),
                        choices,
                        // right answer is picked in the preview
                        right: null,
                        saved: false,
                    });
                    nextNo++;
                });
                if (!this.questions.length) {
                    this.$toast.error("No questions found in the excel sheet", { position: "top", duration: 3000 });
                    this.excelSheet = null;
                }
            } catch (error) {
                this.$toast.error("Invalid excel sheet, please check the file", { position: "top", duration: 3000 });
                this.excelSheet = null;
            }
            this.reading = false;
        },
        rowError(question) {
            if (question.saved) return "";
            if (!this.cellText(question.question_no)) return "Question number is missing";
            if (!this.cellText(question.name)) return "Question is missing";
            const choices = question.choices.filter((choice) => this.cellText(choice.name));
            if (choices.length < 2) return "Add at least two options";
            if (question.choices.length != choices.length) return "Remove the empty option";
            if (question.right == null || !question.choices[question.right]) return "Select the right answer";
            if (this.differentScores) {
                if (!this.cellText(question.maximum_mark) || isNaN(question.maximum_mark)) return "Score for right answer is missing";
                if (this.cellText(question.wrong_answer_mark) && isNaN(question.wrong_answer_mark)) return "Score for wrong answer must be a number";
            }
            return "";
        },
        markToString(mark) {
            return mark == null || mark === "" ? null : `${mark}`;
        },
        // create the question, save its text and scores, then its choices
        async saveQuestion(question) {
            const api = this.$store.getters.getAPIKey.mainAPI;
            const created = await this.$axios.post(`${api}/examquestions`, { dummy_question: true, exam_id: this.exam.id });
            const questionId = created.data.id;
            try {
                await this.$axios.put(`${api}/examquestions/${questionId}`, {
                    question_no: this.cellText(question.question_no),
                    question: [{ type: "text", value: escapeHtml(this.cellText(question.name)) }],
                    maximum_mark: this.markToString(this.differentScores ? question.maximum_mark : this.examScores.positive_mark),
                    wrong_answer_mark: this.markToString(this.differentScores ? question.wrong_answer_mark : this.examScores.negative_mark),
                });
                await this.$axios.post(`${api}/examquestions/${questionId}/examchoices/storemany`, {
                    examChoices: question.choices.map((choice, index) => ({
                        choice_no: this.choiceNo(choice, index),
                        name: this.cellText(choice.name),
                        is_right_choice: index == question.right ? 1 : 0,
                    })),
                });
            } catch (error) {
                // don't leave a half imported question, it is created again on retry
                await this.$axios.delete(`${api}/examquestions/${questionId}`).catch(() => { });
                throw error;
            }
        },
        async importQuestions() {
            if (this.invalidCount) return;
            this.saving = true;
            let failed = 0;
            for (const question of this.questions) {
                if (question.saved) continue;
                try {
                    await this.saveQuestion(question);
                    question.saved = true;
                } catch (error) {
                    failed++;
                }
            }
            this.saving = false;
            this.$emit("imported");
            if (failed) {
                this.$toast.error(`${failed} questions failed to import, please try again`, { position: "top", duration: 4000 });
                return;
            }
            this.$toast.success("Questions imported successfully", { position: "top", duration: 3000 });
            this.$bvModal.hide("mcqImportExcelModal");
        },
        downloadSample() {
            const sheet = XLSX.utils.aoa_to_sheet([this.sampleHeaders, ...this.sampleRows]);
            sheet["!cols"] = [{ wch: 6 }, { wch: 50 }, { wch: 40 }, { wch: 20 }, { wch: 20 }];
            const book = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(book, sheet, "Questions");
            XLSX.writeFile(book, "mcq_questions_sample.xlsx");
        },
    },
};
</script>

<style scoped>
.mcq-import {
    padding: 12px 16px 16px;
    font-size: 13px;
    color: #2d3748;
}

.mcq-import i {
    font-size: 12px !important;
}

.mcq-import-file {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 12px;
}

.mcq-import-file-name {
    font-weight: 700;
}

.mcq-import-note {
    padding: 10px 12px;
    color: #4a5568;
    background: #f7f9fc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.mcq-import-format {
    font-size: 12px;
    background: #fff;
}

.mcq-import-format th {
    background: #edf2f7;
    white-space: nowrap;
}

.mcq-import-loading {
    text-align: center;
    padding: 20px 0;
    color: #718096;
}

.mcq-import-summary {
    display: flex;
    gap: 4px;
    margin-bottom: 8px;
}

.mcq-import-card {
    padding: 10px 12px;
    margin-bottom: 8px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.mcq-import-card-error {
    border-color: #f5b5ad;
}

.mcq-import-card-done {
    background: #f6fbf7;
    border-color: #b9e2c4;
}

.mcq-import-card-head {
    display: flex;
    align-items: center;
    gap: 6px;
}

.mcq-import-qno {
    width: 70px;
    flex-shrink: 0;
    text-align: center;
}

.mcq-import-score {
    width: 80px;
    flex-shrink: 0;
    text-align: center;
}

.mcq-import-choices {
    margin: 6px 0 0 76px;
}

.mcq-import-choice {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    margin-bottom: 2px;
    border-radius: 6px;
}

.mcq-import-choice-no {
    width: 52px;
    flex-shrink: 0;
    text-align: center;
    font-weight: 600;
}

.mcq-choice-right {
    background: #e3f6e8;
}

.mcq-import-add {
    font-size: 12px;
}

.mcq-import-error {
    margin: 4px 0 0 76px;
    font-size: 12px;
    color: #b42318;
}

.mcq-import-foot {
    position: sticky;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0 0;
    background: #fff;
}

.mcq-btn {
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 600;
}

.mcq-chip {
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    color: #4a5568;
    background: #edf2f7;
    border-radius: 999px;
}

.mcq-chip-success {
    color: #22753a;
    background: #e3f6e8;
}

.mcq-chip-danger {
    color: #b42318;
    background: #fdecea;
}

.mcq-muted {
    color: #718096;
}

@media (max-width: 576px) {
    .mcq-import-card-head {
        flex-wrap: wrap;
    }

    .mcq-import-choices,
    .mcq-import-error {
        margin-left: 0;
    }
}
</style>
